import { useRouteContext } from '@/app/layouts/TenantLayout'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import {
	SidebarGroup,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
} from '@/components/ui/sidebar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { getNavRouteTarget, resolveActualUrl } from '@/lib/navRoutes'
import { isOrganizationLevelRoute, stripScopePrefix, toOrgSegment, toTenantSegment } from '@/lib/routeScope'
import { Link, useLocation, useRouter, useSearch } from '@tanstack/react-router'
import { Settings } from 'lucide-react'

import { PlayFilledIcon } from '@/components/icons/PlayFilledIcon'
import { type NavItem, type NavSettingsOption, isUnderBasePath } from '@/data/nav-data'
import { cn } from '@/lib/utils'
import { isPlatformUser, resolveDefaultTenantForOrg } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { useMemo, useState } from 'react'

/** Nav item titles that render as a single link (no expandable children) in project nav */
const HIDE_CHILDREN_TITLES = new Set(['Device Management', 'Audit Logs', 'Sessions'])

type NavMainProps = {
	readonly items: NavItem[]
	/** When true, all items render as single links (e.g. settings, organization nav) */
	readonly hideAllChildren?: boolean
}

const getBasePath = (path: string) => {
	const normalizedPath = stripScopePrefix(path)
	const segments = normalizedPath.split('/').filter(Boolean)
	if (segments.length <= 1) {
		return normalizedPath
	}
	return `/${segments.slice(0, -1).join('/')}`
}

const shouldUseBasePathFallback = (path: string) => {
	const normalizedPath = stripScopePrefix(path)
	const segments = normalizedPath.split('/').filter(Boolean)
	return segments.length > 2
}

const isSectionPathActive = (path: string, currentPath: string) => {
	const normalizedCurrentPath = stripScopePrefix(currentPath)
	const normalizedNavPath = stripScopePrefix(path)

	if (isUnderBasePath(normalizedCurrentPath, normalizedNavPath)) {
		return true
	}

	// A tab strip stays lit while the URL sits on a sibling tab, but only for a path deep enough to have a
	// parent worth matching: `/device-management/all` would otherwise claim every module beside it.
	if (!shouldUseBasePathFallback(normalizedNavPath)) {
		return false
	}

	return isUnderBasePath(normalizedCurrentPath, getBasePath(normalizedNavPath))
}

// Settings tabs share a /settings prefix, so their section is 2 segments deep
// (/settings/users); 1 segment (/settings) would highlight every settings tab.
const getOrgSectionRoot = (normalizedPath: string) => {
	const segments = normalizedPath.split('/').filter(Boolean)
	const depth = segments[0] === 'settings' ? 2 : 1
	return `/${segments.slice(0, depth).join('/')}`
}

// relatedPaths[0] is the item's own URL; the rest are its settings options.
export const isSingleLinkNavItemActive = (relatedPaths: string[], currentPath: string): boolean => {
	if (relatedPaths.some(path => isSectionPathActive(path, currentPath))) {
		return true
	}

	// Org sections resolve to a default tab (/meters → /meters/devices), so match the section
	// root to keep sibling tabs highlighted. Org-only; project nav keeps stricter per-item matching.
	const actualUrl = relatedPaths[0]
	if (!actualUrl || !isOrganizationLevelRoute(actualUrl)) {
		return false
	}
	return isUnderBasePath(stripScopePrefix(currentPath), getOrgSectionRoot(stripScopePrefix(actualUrl)))
}

function NavMain({ items, hideAllChildren = false }: NavMainProps) {
	const { tenantId: routeTenantId, orgId: routeOrgId } = useRouteContext()
	const storeTenantId = useAuthStore(s => s.currentTenant)
	const storeOrgId = useAuthStore(s => s.currentOrganizationId)
	const user = useAuthStore(s => s.user)
	const orgId = routeOrgId || (storeOrgId ? toOrgSegment(storeOrgId) : undefined)
	// On org-level URLs the tenantId param is absent. Resolve a fallback so project-scoped
	// nav links still build a valid /orgs/$orgId/projects/$tenantId/... URL:
	// 1) store tenant, only if it belongs to current org (prevents cross-org leak)
	// 2) first accessible tenant in current org
	const fallbackTenantId = useMemo(() => {
		if (!orgId || !user || !isPlatformUser(user)) {
			return undefined
		}
		if (storeTenantId && user.tenant_list?.some(t => t.id === storeTenantId && t.org_id === orgId)) {
			return toTenantSegment(storeTenantId)
		}
		const defaultTenantId = resolveDefaultTenantForOrg(user, orgId)
		return defaultTenantId ? toTenantSegment(defaultTenantId) : undefined
	}, [storeTenantId, orgId, user])
	const tenantId = routeTenantId || fallbackTenantId
	const shouldHideChildren = (item: NavItem) => hideAllChildren || HIDE_CHILDREN_TITLES.has(item.title)
	const getRouteParams = (itemPath: string) => getNavRouteTarget(itemPath, { orgId, tenantId, routeTenantId })

	return (
		<SidebarGroup>
			<SidebarMenu>
				{items.map(item => (
					<NavItemComponent
						key={item.title}
						item={item}
						getRouteParams={getRouteParams}
						hideChildren={shouldHideChildren}
					/>
				))}
			</SidebarMenu>
		</SidebarGroup>
	)
}

function NavItemComponent({
	item,
	getRouteParams,
	hideChildren,
}: {
	item: NavItem
	getRouteParams: (path: string) => { to: string; params: Record<string, string> | undefined }
	hideChildren: (item: NavItem) => boolean
}) {
	const currentSearch = useSearch({ strict: false })
	const router = useRouter()
	const location = useLocation()
	const currentPath = location.pathname
	const showSettingsInline = Boolean(item.hasSettings && item.settingsOptions?.length)
	const [settingItemOpen, setSettingItemOpen] = useState(false)

	const settingsOptionList = item.settingsOptions ?? []

	const navigateToSettingsOption = (option: NavSettingsOption) => {
		const routeInfo = getRouteParams(option.path)
		if (routeInfo.params) {
			router.navigate({ to: routeInfo.to, params: routeInfo.params })
		} else {
			router.navigate({ to: routeInfo.to })
		}
	}

	const renderSettingsDropdown = () => {
		if (!showSettingsInline) {
			return null
		}

		// A lone settings option navigates directly — no dropdown to open. The tooltip
		// names the destination so the gear isn't an unlabelled mystery icon.
		if (settingsOptionList.length === 1) {
			const option = settingsOptionList[0]
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							type='button'
							aria-label={option.label}
							className='ml-1 opacity-0 cursor-pointer p-1 rounded-sm transition-opacity duration-200 group-hover/sidebar-btn:opacity-100 group-data-[collapsible=icon]/sidebar:hidden'
							onClick={e => {
								e.preventDefault()
								e.stopPropagation()
								navigateToSettingsOption(option)
							}}>
							<Settings className='size-(--icon-standalone)' />
						</button>
					</TooltipTrigger>
					<TooltipContent side='right' sideOffset={16} hideArrow>
						{option.label}
					</TooltipContent>
				</Tooltip>
			)
		}

		return (
			<DropdownMenu open={settingItemOpen} onOpenChange={setSettingItemOpen}>
				<DropdownMenuTrigger asChild>
					<div className='ml-1 opacity-0 cursor-pointer p-1 rounded-sm transition-opacity duration-200 group-hover/sidebar-btn:opacity-100 group-data-[collapsible=icon]/sidebar:hidden'>
						<Settings className='size-(--icon-standalone)' />
					</div>
				</DropdownMenuTrigger>
				<DropdownMenuContent side='left' align='start' className='space-y-2'>
					{item.settingsOptions?.map(option => {
						const routeInfo = getRouteParams(option.path)
						const actualUrl = resolveActualUrl(routeInfo)
						const isActive = isSectionPathActive(actualUrl, currentPath)

						return (
							<DropdownMenuItem
								key={option.label}
								className={cn(
									'gap-2 cursor-pointer text-body h-control! min-h-control!',
									isActive
										? 'bg-sidebar-accent text-header-tab-active-text font-medium hover:bg-sidebar-accent-hover focus:bg-sidebar-accent-hover'
										: 'text-header-tab-inactive-text hover:bg-sidebar-accent focus:bg-sidebar-accent hover:text-header-tab-active-text focus:text-header-tab-active-text',
								)}
								onClick={e => {
									e.preventDefault()
									e.stopPropagation() // Prevent collapsing
									if (routeInfo.params) {
										router.navigate({ to: routeInfo.to, params: routeInfo.params })
									} else {
										router.navigate({ to: routeInfo.to })
									}
									// close the dropdown
									setSettingItemOpen(false)
								}}>
								{option.icon && (
									<option.icon
										className={cn(
											'size-(--icon-standalone) shrink-0',
											isActive ? 'text-header-tab-active-text' : 'text-header-tab-inactive-text',
										)}
									/>
								)}
								{option.label}
							</DropdownMenuItem>
						)
					})}
				</DropdownMenuContent>
			</DropdownMenu>
		)
	}

	if (!item.items?.length || hideChildren(item)) {
		const routeInfo = getRouteParams(item.path)
		const actualUrl = resolveActualUrl(routeInfo)

		const relatedPaths = [
			actualUrl,
			...(item.settingsOptions?.map(option => resolveActualUrl(getRouteParams(option.path))) ?? []),
		]

		const isActive = isSingleLinkNavItemActive(relatedPaths, currentPath)
		return (
			<SidebarMenuItem>
				<div className='flex items-center group/sidebar-btn ml-1'>
					<Link to={routeInfo.to} params={routeInfo.params} search={currentSearch} preload='intent' className='flex-1'>
						<SidebarMenuButton asChild isActive={isActive} className='flex-1'>
							<div className='flex items-center gap-1.5 flex-1'>
								{item.icon && (
									<item.icon
										className={cn(
											'size-(--icon-standalone) shrink-0',
											isActive
												? 'text-sidebar-active-text'
												: 'text-sidebar-inactive-text group-hover/sidebar-btn:text-sidebar-active-text',
										)}
									/>
								)}
								<span
									className={cn(
										'flex-1 text-body',
										isActive
											? 'text-sidebar-active-text'
											: 'text-sidebar-inactive-text group-hover/sidebar-btn:text-sidebar-active-text',
									)}>
									{item.title}
								</span>
							</div>
						</SidebarMenuButton>
					</Link>
					{renderSettingsDropdown()}
				</div>
			</SidebarMenuItem>
		)
	}

	return (
		<Collapsible asChild defaultOpen={true} className='group/collapsible'>
			<SidebarMenuItem>
				<div className='flex items-center group/sidebar-btn mb-1'>
					<CollapsibleTrigger asChild>
						<SidebarMenuButton className='flex'>
							<div className='flex items-baseline gap-2 justify-start flex-1'>
								<span className='align-middle text-body text-sidebar-inactive-text group-hover/sidebar-btn:text-sidebar-active-text'>
									{item.title}
								</span>
								<PlayFilledIcon
									className={cn(
										'size-2.5 shrink-0 align-middle transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 text-sidebar-inactive-text group-hover/sidebar-btn:text-sidebar-active-text',
									)}
								/>
							</div>
						</SidebarMenuButton>
					</CollapsibleTrigger>
					<div className='ml-1 w-6 h-6 flex items-center justify-center'>{renderSettingsDropdown()}</div>
				</div>

				<CollapsibleContent>
					<SidebarMenuSub>
						{item.items.map(subItem => {
							const routeInfo = getRouteParams(subItem.path)
							const actualUrl = resolveActualUrl(routeInfo)

							const isActive = isSectionPathActive(actualUrl, currentPath)
							return (
								<SidebarMenuSubItem key={subItem.title}>
									<SidebarMenuSubButton asChild isActive={isActive}>
										<Link
											to={routeInfo.to}
											params={routeInfo.params}
											search={currentSearch}
											preload='intent'
											className='flex items-center gap-1.5 px-3 ml-1'>
											{subItem.icon && (
												<subItem.icon
													className={cn(
														'size-(--icon-standalone) shrink-0',
														isActive
															? 'text-sidebar-active-text!'
															: 'text-sidebar-inactive-text! group-hover/menu-sub-item:text-sidebar-active-text!',
													)}
												/>
											)}
											<span
												className={cn(
													'text-body',
													isActive
														? 'text-sidebar-active-text'
														: 'text-sidebar-inactive-text group-hover/menu-sub-item:text-sidebar-active-text',
												)}>
												{subItem.title}
											</span>
										</Link>
									</SidebarMenuSubButton>
								</SidebarMenuSubItem>
							)
						})}
					</SidebarMenuSub>
				</CollapsibleContent>
			</SidebarMenuItem>
		</Collapsible>
	)
}

export { NavMain }
